import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { ApiService, Recueil } from '@lyricstream/shared';
import { TranslateService } from '@ngx-translate/core';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { SeoAlternate, SeoService } from '../services/seo.service';

const LANGS = ['fr', 'ht', 'en', 'es'];
const DEFAULT_LANG = 'fr';
/** Au-delà, la page part avec le titre générique de la rubrique plutôt que d'attendre l'API. */
const FETCH_TIMEOUT_MS = 4000;

/** Langue et chemin sans langue (« /popular ») d'une URL « /fr/popular?x=1 ». */
function splitUrl(url: string): { lang: string; path: string } {
  const clean = url.split(/[?#]/)[0];
  const [, lang = DEFAULT_LANG, ...rest] = clean.split('/');
  return { lang, path: rest.length ? `/${rest.join('/')}` : '' };
}

/** Alternates hreflang d'une page d'INTERFACE (jamais pour un contenu comme un chant ou un recueil). */
function interfaceAlternates(path: string): SeoAlternate[] {
  return [
    ...LANGS.map((l) => ({ lang: l, path: `/${l}${path}` })),
    { lang: 'x-default', path: `/${DEFAULT_LANG}${path}` },
  ];
}

/**
 * Balises SEO d'une page d'interface (accueil, Découvrir, populaires, récents, recueils, recherche, premium,
 * conditions, confidentialité…). Textes dans `SEO.<clé>.TITLE|DESCRIPTION` (i18n) ; la langue est déjà chargée par
 * `langGuard`, donc le titre est traduit dans le HTML serveur comme dans le navigateur.
 * `withAlternates = false` pour un contenu (ex. playlist) : l'hreflang ne concerne que les pages d'interface.
 * Ne bloque jamais la navigation.
 */
export const pageSeoResolver = (key: string, withAlternates = true): ResolveFn<boolean> => (_route, state) => {
  const seo = inject(SeoService);
  const translate = inject(TranslateService);
  const { lang, path } = splitUrl(state.url);
  const title = translate.instant(`SEO.${key}.TITLE`);
  seo.set({
    title: `${title} — LyricStream`,
    description: translate.instant(`SEO.${key}.DESCRIPTION`),
    path: `/${lang}${path}`,
    lang,
    alternates: withAlternates ? interfaceAlternates(path) : undefined,
  });
  return true;
};

/**
 * Balises SEO d'un contenu nommé dont le nom vient de l'URL (profil de contributeur) ou est connu d'avance :
 * pas d'hreflang, le contenu a sa propre langue. `param` = paramètre de route qui porte le nom.
 */
export const namedPageSeoResolver = (key: string, param: string): ResolveFn<boolean> => (route, state) => {
  const seo = inject(SeoService);
  const translate = inject(TranslateService);
  const { lang, path } = splitUrl(state.url);
  const name = decodeURIComponent(route.paramMap.get(param) ?? '');
  seo.set({
    title: `${translate.instant(`SEO.${key}.TITLE`, { name })} — LyricStream`,
    description: translate.instant(`SEO.${key}.DESCRIPTION`, { name }),
    path: `/${lang}${path}`,
    lang,
  });
  return true;
};

/**
 * Balises SEO d'un recueil : retrouve son nom dans `/api/recueil/active` (le même appel que le sitemap).
 * Recueil introuvable ou API lente/en panne : on retombe sur les balises de la liste des recueils, sans faux 404.
 */
export const recueilSeoResolver: ResolveFn<boolean> = async (route, state) => {
  const seo = inject(SeoService);
  const translate = inject(TranslateService);
  const api = inject(ApiService);
  const { lang, path } = splitUrl(state.url);
  const id = decodeURIComponent(route.paramMap.get('id') ?? '');

  const recueils = await firstValueFrom(
    api.get<Recueil[]>('/api/recueil/active').pipe(timeout(FETCH_TIMEOUT_MS), catchError(() => of([] as Recueil[]))),
  );
  const found = recueils.find((r) => String(r.code) === id || r.slug === id);
  const key = found ? 'RECUEIL' : 'RECUEILS';
  const params = { name: found?.name ?? '' };

  seo.set({
    title: `${translate.instant(`SEO.${key}.TITLE`, params)} — LyricStream`,
    description: translate.instant(`SEO.${key}.DESCRIPTION`, params),
    // Adresse canonique : le slug quand il existe, que l'on arrive par le code ou par le slug.
    path: found?.slug ? `/${lang}/recueils/${encodeURIComponent(found.slug)}` : `/${lang}${path}`,
    image: found?.imagePathUrl,
    lang,
  });
  return true;
};
